import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FiX, FiInfo, FiLayers } from 'react-icons/fi';
import { BED_CONFIGURATIONS } from '../../utils/constants';
import { bedDispositionsAPI, roomGroupsAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { Modal } from '../../design-system';

const RoomForm = ({ room, onSave, onClose, existingRooms = [] }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bedDispositions, setBedDispositions] = useState([]);
  const [selectedDispositions, setSelectedDispositions] = useState([]);
  const [loadingDispositions, setLoadingDispositions] = useState(true);
  const [roomGroups, setRoomGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(room?.room_group_id || null);
  const isEditing = !!room;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      number: room?.number || '',
      bed_count: room?.bed_count || 1,
      cleaning_time_minutes: room?.cleaning_time_minutes || 30,
      notes: room?.notes || '',
    },
  });

  const bedCount = watch('bed_count');

  useEffect(() => {
    fetchBedDispositions();
    fetchRoomGroups();
  }, []);

  useEffect(() => {
    // Initialize selected dispositions when room data is loaded
    if (room?.bed_dispositions) {
      setSelectedDispositions(room.bed_dispositions.map(d => d.id));
    }
  }, [room]);

  // Uncheck dispositions that become disabled when bed count changes
  useEffect(() => {
    if (bedCount && bedDispositions.length > 0) {
      const count = parseInt(bedCount);
      const newSelectedDispositions = selectedDispositions.filter(id => {
        const disposition = bedDispositions.find(d => d.id === id);
        if (!disposition) return false;
        // Keep only if still valid for current bed count
        if (disposition.min_beds > count) return false;
        if (disposition.max_beds !== null && disposition.max_beds !== undefined && disposition.max_beds < count) return false;
        return true;
      });

      if (newSelectedDispositions.length !== selectedDispositions.length) {
        setSelectedDispositions(newSelectedDispositions);
      }
    }
  }, [bedCount, bedDispositions]);

  const fetchBedDispositions = async () => {
    try {
      const dispositions = await bedDispositionsAPI.getAll();
      setBedDispositions(dispositions);
    } catch (error) {
      console.error('Error fetching bed dispositions:', error);
    } finally {
      setLoadingDispositions(false);
    }
  };

  const fetchRoomGroups = async () => {
    try {
      const groups = await roomGroupsAPI.getAll();
      setRoomGroups(groups);
    } catch (error) {
      console.error('Error fetching room groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleDisposition = (dispositionId) => {
    setSelectedDispositions(prev => {
      if (prev.includes(dispositionId)) {
        return prev.filter(id => id !== dispositionId);
      } else {
        return [...prev, dispositionId];
      }
    });
  };

  const isDispositionDisabled = (disposition) => {
    const count = parseInt(bedCount);
    // Check minimum beds
    if (disposition.min_beds > count) return true;
    // Check maximum beds if defined
    if (disposition.max_beds !== null && disposition.max_beds !== undefined && disposition.max_beds < count) return true;
    return false;
  };

  const onSubmit = async (data) => {
    if (selectedDispositions.length === 0) {
      alert('Debes seleccionar al menos una disposición de camas');
      return;
    }

    setIsSubmitting(true);

    try {
      const roomData = {
        number: data.number,
        bed_count: parseInt(data.bed_count),
        cleaning_time_minutes: parseInt(data.cleaning_time_minutes),
        bed_disposition_ids: selectedDispositions,
        room_group_id: selectedGroupId,
        notes: data.notes || null,
        is_active: true
      };

      await onSave(roomData);
    } catch (error) {
      console.error('Error saving room:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Habitación' : 'Nueva Habitación'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="max-h-[60vh] overflow-y-auto">
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="room-form">
              {/* Room number */}
              <div>
                <label htmlFor="number" className="form-label">
                  Número de habitación *
                </label>
                <input
                  id="number"
                  type="text"
                  className={`form-input ${errors.number ? 'border-danger-500' : ''}`}
                  placeholder="101, 102A, etc."
                  {...register('number', {
                    required: 'El número de habitación es requerido',
                    minLength: { value: 1, message: 'Debe tener al menos 1 carácter' },
                    validate: (value) => {
                      // Check if room number already exists (excluding current room when editing)
                      const trimmedValue = value.trim().toLowerCase();
                      const isDuplicate = existingRooms.some(
                        r => r.number.toLowerCase() === trimmedValue && r.id !== room?.id
                      );
                      return !isDuplicate || 'Ya existe una habitación con este número';
                    }
                  })}
                />
                {errors.number && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.number.message}
                  </p>
                )}
              </div>

              {/* Room Group */}
              <div>
                <label htmlFor="room_group" className="form-label flex items-center space-x-2">
                  <FiLayers className="h-4 w-4 text-purple-500" />
                  <span>Grupo de habitaciones</span>
                </label>
                <select
                  id="room_group"
                  className="form-input"
                  value={selectedGroupId || ''}
                  onChange={(e) => setSelectedGroupId(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={loadingGroups}
                >
                  <option value="">Sin grupo asignado</option>
                  {roomGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Opcional. Los grupos organizan las habitaciones en la tabla de órdenes.
                </p>
              </div>

              {/* Bed count */}
              <div>
                <label htmlFor="bed_count" className="form-label">
                  Número de camas *
                </label>
                <input
                  id="bed_count"
                  type="number"
                  min="1"
                  max="10"
                  className={`form-input ${errors.bed_count ? 'border-danger-500' : ''}`}
                  placeholder="1"
                  {...register('bed_count', {
                    required: 'El número de camas es requerido',
                    min: { value: 1, message: 'Debe tener al menos 1 cama' },
                    max: { value: 10, message: 'Máximo 10 camas' }
                  })}
                />
                {errors.bed_count && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.bed_count.message}
                  </p>
                )}
              </div>

              {/* Cleaning time in minutes */}
              <div>
                <label htmlFor="cleaning_time_minutes" className="form-label">
                  Tiempo de limpieza (minutos) *
                </label>
                <input
                  id="cleaning_time_minutes"
                  type="number"
                  min="5"
                  max="180"
                  className={`form-input ${errors.cleaning_time_minutes ? 'border-danger-500' : ''}`}
                  placeholder="30"
                  {...register('cleaning_time_minutes', {
                    required: 'El tiempo de limpieza es requerido',
                    min: { value: 5, message: 'Debe ser al menos 5 minutos' },
                    max: { value: 180, message: 'Máximo 180 minutos (3 horas)' }
                  })}
                />
                {errors.cleaning_time_minutes && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.cleaning_time_minutes.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Tiempo estimado que se tarda en limpiar esta habitación
                </p>
              </div>

              {/* Bed Dispositions Selection */}
              <div>
                <label className="form-label">
                  Disposiciones de camas * {loadingDispositions && <span className="text-sm text-gray-500">(Cargando...)</span>}
                </label>
                {!loadingDispositions && (
                  <div className="space-y-2">
                    {bedDispositions.length === 0 ? (
                      <div className="text-sm text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <FiInfo className="inline h-4 w-4 text-yellow-600 mr-2" />
                        No hay disposiciones de camas configuradas.
                        Por favor, ve a la pestaña "Disposiciones de Camas" para crear una.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-2">
                        {bedDispositions.map((disposition) => {
                          const isDisabled = isDispositionDisabled(disposition);
                          return (
                            <label
                              key={disposition.id}
                              className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-colors ${
                                isDisabled
                                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                  : selectedDispositions.includes(disposition.id)
                                  ? 'border-blue-500 bg-blue-50 cursor-pointer'
                                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDispositions.includes(disposition.id)}
                                onChange={() => toggleDisposition(disposition.id)}
                                disabled={isDisabled}
                                className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <div className="flex-1">
                                <div className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                  {disposition.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {disposition.min_beds === disposition.max_beds ? (
                                    // Exactamente una cantidad específica
                                    <>Exactamente {disposition.min_beds} cama{disposition.min_beds > 1 ? 's' : ''}</>
                                  ) : disposition.max_beds ? (
                                    // Rango definido
                                    <>De {disposition.min_beds} a {disposition.max_beds} camas</>
                                  ) : (
                                    // Sin límite máximo
                                    <>{disposition.min_beds} o más camas</>
                                  )}
                                </div>
                                {(parseFloat(disposition.sheet_cost || 0) > 0 || parseFloat(disposition.towel_cost || 0) > 0) && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Sábanas: {parseFloat(disposition.sheet_cost || 0).toFixed(2)}€ | Toallas: {parseFloat(disposition.towel_cost || 0).toFixed(2)}€
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="form-label">
                  Notas
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  className="form-input"
                  placeholder="Notas adicionales sobre la habitación..."
                  {...register('notes')}
                />
              </div>

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="room-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoomForm;